import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { useTypedSelector } from '../../../shared/hooks';
import AlertService from '../../../shared/services/AlertService';
import { alertQueueActions } from '../../../shared/store/alertQueueSlice';
import { type RootState } from '../../../shared/types';
import { licenseExportActions } from '../data-layer/licenseExportSlice';
import { LICENSE_INTELLIGENCE_EXPORT_CREATE_RESPONSE } from '../mock-data/licenseIntelligenceSimulationExportData';
import type { CreateLicenseExportRequest } from '../types';

/**
 * Hook to trigger a License export and handle success/error banners.
 * Mirrors the ViolationsReport `useExportReport` hook but simplified
 * since License export doesn't have reportType variants.
 */
const useLicenseExportReport = () => {
  const dispatch = useDispatch();
  // Mock create export mutation
  const createLicenseExport = useCallback(
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    (_payload: CreateLicenseExportRequest) => ({
      unwrap: () =>
        Promise.resolve(LICENSE_INTELLIGENCE_EXPORT_CREATE_RESPONSE),
    }),
    []
  );
  const isLoading = false;

  const { activeExport } = useTypedSelector(
    (state: RootState) => state.aag.licenseExport ?? { activeExport: null }
  );

  const showSuccessBanner = useCallback(() => {
    dispatch(
      alertQueueActions.enqueue(
        AlertService.info(
          'Exporting',
          "License export is running in background. You'll receive an email when your report is ready. You can also download it later from My Downloads in your profile.",
          2000,
          `license-export-info-${Date.now()}`
        )
      )
    );
  }, [dispatch]);

  const showErrorBanner = useCallback(
    (apiMessage?: string) => {
      const message =
        apiMessage && apiMessage.trim().length > 0
          ? apiMessage
          : 'License export failed. Please try again or contact support if the issue persists.';

      dispatch(
        alertQueueActions.enqueue(
          AlertService.error(
            'Export failed',
            message,
            5000,
            `license-export-error-${Date.now()}`
          )
        )
      );
    },
    [dispatch]
  );

  const exportLicenseInventory = useCallback(
    async (payload: CreateLicenseExportRequest) => {
      // Prevent duplicate exports
      if (activeExport) {
        dispatch(
          alertQueueActions.enqueue(
            AlertService.info(
              'Export in Progress',
              'License export is already in progress. Please wait for it to complete.',
              2000,
              `license-export-in-progress-${Date.now()}`
            )
          )
        );

        return;
      }

      try {
        // Reset progress to 0 before starting
        dispatch(licenseExportActions.setExportProgress(0));

        await createLicenseExport(payload).unwrap();

        // Mark active export for polling
        dispatch(licenseExportActions.setActiveExport({ progress: 0 }));

        showSuccessBanner();
      } catch (error) {
        Logger.log('error', 'License Export failed:', error);

        const GENERIC_ERROR_PATTERNS = [
          'Request failed with status code',
          'Failed to create license export',
          'Invalid response format',
        ];

        const isGenericMessage = (msg: string): boolean =>
          GENERIC_ERROR_PATTERNS.some((pattern) => msg.includes(pattern));

        /**
         * Recursively extracts error message from various error structures.
         */
        const extractErrorMessage = (input: unknown): string | null => {
          if (typeof input === 'string') {
            return input.trim().length > 0 ? input.trim() : null;
          }

          if (!input || typeof input !== 'object') {
            return null;
          }

          const obj = input as Record<string, unknown>;

          if ('message' in obj) {
            const { message } = obj;

            if (typeof message === 'string') {
              const trimmed = message.trim();

              if (trimmed.length > 0 && !isGenericMessage(trimmed)) {
                return trimmed;
              }
            }
          }

          const dataSources: unknown[] = [];

          if ('data' in obj) {
            dataSources.push(obj.data);
          }

          if (
            'response' in obj &&
            obj.response &&
            typeof obj.response === 'object'
          ) {
            const response = obj.response as Record<string, unknown>;

            if ('data' in response) {
              dataSources.push(response.data);
            }
          }

          const extractedMessage = dataSources
            .map((data) => extractErrorMessage(data))
            .find((msg) => msg !== null);

          if (extractedMessage) {
            return extractedMessage;
          }

          return null;
        };

        const errorMessage = extractErrorMessage(error) ?? '';

        Logger.log(
          'error',
          'Extracted license export error message:',
          errorMessage
        );

        showErrorBanner(errorMessage);
      }
    },
    [
      createLicenseExport,
      showSuccessBanner,
      showErrorBanner,
      activeExport,
      dispatch,
    ]
  );

  const isExportDisabled = Boolean(activeExport);

  return {
    exportLicenseInventory,
    isExporting: isLoading,
    isExportDisabled,
  };
};

export default useLicenseExportReport;
